import React, { useState, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  useMediaQuery
} from '@mui/material';
import { ArrowUpward, ArrowDownward, DragIndicator } from '@mui/icons-material';
import { useDashboardFooter } from '../../contexts/DashboardFooterContext';
import { ProjectRanker } from '../expertise-quiz/index';
import { sanitizeStyleValue } from '../../utils/style-utils';

export interface DesignerStyleQuizResult {
  canDesignAnyStyle: boolean | null;
  hasKnownStyle: boolean | null;
  knownStyle?: string;
  expertiseStyles: string[];
  styleRankings?: string[];
}

export interface DesignerStyleQuizRef {
  nextStep: () => void;
  backStep: () => void;
}

export interface DesignerStyleQuizProps {
  onComplete?: (result: DesignerStyleQuizResult) => void;
  onStepChange?: (step: number, total: number) => void;
  onResultChange?: (result: DesignerStyleQuizResult) => void;
  initialData?: DesignerStyleQuizResult | null;
}

const STYLE_LABELS = [
  'Bohemian', 'Scandinavian', 'Minimalist', 'Contemporary', 'Modern', 'MidCentury', 'Transitional',
  'Modern Farmhouse', 'Eclectic', 'Rustic', 'Shabby Chic', 'Maximalism', 'Art Deco', 'Industrial', 'Traditional',
  'Coastal', 'Farmhouse', 'French Country', 'Hollywood Regency', 'Other'
];

const DESIGN_STYLES = STYLE_LABELS.map((label) => ({
  value: sanitizeStyleValue(label),
  label,
}));

const getDesignStyleLabel = (value: string) => {
  const match = DESIGN_STYLES.find((option) => option.value === value);
  if (match) return match.label;
  return value;
};

const normalizeStyleArray = (values?: string[] | null): string[] =>
  (values || [])
    .map((value) => sanitizeStyleValue(value))
    .filter((value) => Boolean(value));

const DesignerStyleQuiz = forwardRef<DesignerStyleQuizRef, DesignerStyleQuizProps>(({
  onComplete,
  onStepChange,
  onResultChange,
  initialData
}, ref) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  
  // Screen 1 state - initialize from initialData if provided
  const [canDesignAnyStyle, setCanDesignAnyStyle] = useState<boolean | null>(
    initialData?.canDesignAnyStyle ?? null
  );
  
  // Screen 2 state - initialize from initialData if provided
  const [hasKnownStyle, setHasKnownStyle] = useState<boolean | null>(
    initialData?.hasKnownStyle ?? null
  );
  const [knownStyle, setKnownStyle] = useState<string>(
    sanitizeStyleValue(initialData?.knownStyle || '')
  );
  
  // Screen 3 state - initialize from initialData if provided
  const [expertiseStyles, setExpertiseStyles] = useState<string[]>(
    normalizeStyleArray(initialData?.expertiseStyles)
  );
  
  // Screen 4 state (ranking) - initialize from initialData if provided
  const [styleRankings, setStyleRankings] = useState<string[]>(
    normalizeStyleArray(initialData?.styleRankings)
  );
  
  const { setFooter, clearFooter } = useDashboardFooter();

  const handleNext = useCallback(() => {
    // Prevent advancing if no styles are selected on the expertise styles step
    if (currentStep === 3 && expertiseStyles.length === 0) {
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      onStepChange?.(currentStep + 1, totalSteps);
    } else {
      // Complete the quiz
      const result: DesignerStyleQuizResult = {
        canDesignAnyStyle: canDesignAnyStyle || false,
        hasKnownStyle: hasKnownStyle || false,
        knownStyle: hasKnownStyle ? knownStyle : undefined,
        expertiseStyles,
        styleRankings
      };
      onComplete?.(result);
    }
  }, [currentStep, totalSteps, canDesignAnyStyle, hasKnownStyle, knownStyle, expertiseStyles, styleRankings, onComplete, onStepChange]);

  // Notify parent about step changes when component loads or step changes
  useEffect(() => {
    onStepChange?.(currentStep, totalSteps);
  }, [currentStep, totalSteps, onStepChange]);

  // Align rankings with the latest selections
  useEffect(() => {
    setStyleRankings((prev) => {
      if (expertiseStyles.length === 0) {
        return prev.length === 0 ? prev : [];
      }

      const filtered = prev.filter((style) => expertiseStyles.includes(style));
      const missing = expertiseStyles.filter((style) => !filtered.includes(style));

      if (missing.length === 0 && filtered.length === prev.length) {
        return prev;
      }

      return [...filtered, ...missing];
    });
  }, [expertiseStyles]);

  // Notify parent about result changes for validation
  useEffect(() => {
    const result: DesignerStyleQuizResult = {
      canDesignAnyStyle: canDesignAnyStyle !== null ? canDesignAnyStyle : null,
      hasKnownStyle: hasKnownStyle !== null ? hasKnownStyle : null,
      knownStyle: hasKnownStyle ? knownStyle : undefined,
      expertiseStyles,
      styleRankings,
    };
    onResultChange?.(result);
  }, [canDesignAnyStyle, hasKnownStyle, knownStyle, expertiseStyles, styleRankings, onResultChange]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      onStepChange?.(currentStep - 1, totalSteps);
    }
  }, [currentStep, onStepChange]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    nextStep: handleNext,
    backStep: handleBack
  }));

  const toggleExpertiseStyle = (styleValue: string) => {
    const normalizedValue = sanitizeStyleValue(styleValue);
    setExpertiseStyles((prev) => {
      const alreadySelected = prev.includes(normalizedValue);
      return alreadySelected
        ? prev.filter((s) => s !== normalizedValue)
        : [...prev, normalizedValue];
    });
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return canDesignAnyStyle !== null;
      case 2:
        return hasKnownStyle !== null && (!hasKnownStyle || knownStyle);
      case 3:
        return expertiseStyles.length > 0;
      default:
        return true;
    }
  };

  const renderScreen1 = () => (
    <Fade in={true} timeout={300}>
      <Container maxWidth="md" sx={{ textAlign: 'center', pl: 2, pr: 2, pt: 6 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '20.3px', sm: '1.5rem' }
          }}
        >
          I can design to any style preference my client has.
        </Typography>
        
        <Box 
          display="flex" 
          gap={3} 
          justifyContent="center" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          maxWidth="400px"
          mx="auto"
        >
          <Button
            variant={canDesignAnyStyle === true ? 'contained' : 'outlined'}
            onClick={() => setCanDesignAnyStyle(true)}
            color="secondary"
            sx={{
              minWidth: 120,
              height: 60,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2
            }}
          >
            Yes
          </Button>
          
          <Button
            variant={canDesignAnyStyle === false ? 'contained' : 'outlined'}
            onClick={() => setCanDesignAnyStyle(false)}
            color="secondary"
            sx={{
              minWidth: 120,
              height: 60,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2
            }}
          >
            No
          </Button>
        </Box>
      </Container>
    </Fade>
  );

  const renderScreen2 = () => (
    <Fade in={true} timeout={300}>
      <Container maxWidth="md" sx={{ textAlign: 'center', pl: 2, pr: 2, pt: 6 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '20.3px', sm: '1.5rem' }
          }}
        >
          Is there a particular design style that you are known for?
        </Typography>
        
        <Box 
          display="flex" 
          gap={3} 
          justifyContent="center" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          maxWidth="400px"
          mx="auto"
          mb={4}
        >
          <Button
            variant={hasKnownStyle === true ? 'contained' : 'outlined'}
            onClick={() => setHasKnownStyle(true)}
            color="secondary"
            sx={{
              minWidth: 120,
              height: 60,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2
            }}
          >
            Yes
          </Button>
          
          <Button
            variant={hasKnownStyle === false ? 'contained' : 'outlined'}
            onClick={() => setHasKnownStyle(false)}
            color="secondary"
            sx={{
              minWidth: 120,
              height: 60,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2
            }}
          >
            No
          </Button>
        </Box>

        {hasKnownStyle === true && (
          <Box maxWidth="300px" mx="auto">
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Please Specify
            </Typography>
            <FormControl fullWidth>
              <Select
                value={knownStyle}
                onChange={(e) => setKnownStyle(sanitizeStyleValue(e.target.value as string))}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Fixed height for 10 items (30px per item)
                      overflow: 'auto',
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select One...
                </MenuItem>
                {DESIGN_STYLES.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Container>
    </Fade>
  );

  const renderScreen3 = () => (
    <Fade in={true} timeout={300}>
      <Container maxWidth="md" sx={{ textAlign: 'center', pl: 2, pr: 2, pt: 6 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '20.3px', sm: '1.5rem' }
          }}
        >
          Select all design styles where you have strong expertise.
        </Typography>
        
        <Box 
          display="flex" 
          flexWrap="wrap" 
          gap={1} 
          justifyContent="start"
          maxWidth="800px"
          mx="auto"
        >
          {DESIGN_STYLES.map((styleOption) => (
            <Chip
              key={styleOption.value}
              label={styleOption.label}
              onClick={() => toggleExpertiseStyle(styleOption.value)}
              variant={expertiseStyles.includes(styleOption.value) ? 'filled' : 'outlined'}
              color="secondary"
              sx={{
                fontSize: '0.9rem',
                py: 0.5,
                px: 0.5,
                height: 'auto',
                minWidth: 'auto',
                width: 'fit-content',
                borderRadius: 50,
                borderWidth: 1,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            />
          ))}
        </Box>
      </Container>
    </Fade>
  );


  const renderScreen4 = () => (
    <Fade in={true} timeout={300}>
      <Container maxWidth="md" sx={{ textAlign: 'center', pl: 2, pr: 2, pt: 6 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            mb: 2, 
            color: 'text.primary',
            fontSize: { xs: '20.3px', sm: '1.5rem' }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Great! Now, drag and drop your selections to rank them.
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Great! Now, tap items to rank them in order.
          </Box>
        </Typography>

        <ProjectRanker 
          items={expertiseStyles}
          value={styleRankings}
          onChange={setStyleRankings}
          getDisplayLabel={getDesignStyleLabel}
        />
      </Container>
    </Fade>
  );

  return (
    <Box>
      {currentStep === 1 && renderScreen1()}
      {currentStep === 2 && renderScreen2()}
      {currentStep === 3 && renderScreen3()}
      {currentStep === 4 && renderScreen4()}
    </Box>
  );
});

DesignerStyleQuiz.displayName = 'DesignerStyleQuiz';

export { DesignerStyleQuiz };
